"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, Shield, Zap, FileCheck, PhoneCall, Mail, ArrowRight, Loader } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function NarcoticsInquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center bg-secondary/50 rounded-xl border border-border">
        <div className="size-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mb-4">
          <CheckCircle2 className="size-6" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Inquiry Submitted</h3>
        <p className="text-muted-foreground text-sm max-w-md mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="work_email" rules={{
        required: "Work email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField control={control} name="company_name" render={({
        field
      }) => <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Acme Corp" {...field} />
              </FormControl>
              <FormDescription className="text-xs">
                Agency, police force, customs unit, or enterprise
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone_number" render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 1234 5678" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Please detail your application or field requirement"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Message *</FormLabel>
            <FormControl>
              <Textarea placeholder="Your message..." className="min-h-28" {...field} />
            </FormControl>
            <FormDescription className="text-xs">
              Describe target substances (e.g., illicit narcotics, synthetic opioids, cutting agents) or deployment setting.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full text-base font-medium h-11">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Submitting Inquiry...
          </> : <>
            Request Technical Assessment
            <ArrowRight className="size-4 ml-1" />
          </>}
      </Button>
    </Form>;
}
export default function NarcoticsInquiry() {
  const shouldReduceMotion = useReducedMotion();
  const capabilities = [{
    title: "Handheld Raman & NIR Systems",
    desc: "Instant non-contact identification through transparent packaging, vials, and plastic bags without direct exposure.",
    icon: Zap
  }, {
    title: "Extensive Spectral Libraries",
    desc: "Up-to-date libraries for illicit narcotics, novel psychoactive substances (NPS), fentanyl analogues, and precursors.",
    icon: Shield
  }, {
    title: "Evidential Integrity & Qualification",
    desc: "Full IQ/OQ validation protocols and audit-ready data workflows for law enforcement, customs, and forensic labs.",
    icon: FileCheck
  }];
  return <section id="narcoticsidentificationcta" data-nav="light" className="bg-background py-16 lg:py-24 border-t border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          {/* Left Context Column (5 cols) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between h-full">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="secondary" className="px-2.5 py-1 text-xs">
                  Public Safety & Forensic Screening
                </Badge>
              </div>

              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default leading-tight mb-4">
                Rapid narcotics identification for field and laboratory operations
              </h2>

              <p className="text-muted-foreground text-base leading-relaxed mb-6">
                Antech Solutions delivers robust analytical instrumentation for rapid identification of controlled substances, synthetic opioids, and cutting agents across Ireland and the UK.
              </p>

              <div className="space-y-4 mb-8">
                {capabilities.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="flex gap-3.5 items-start p-3.5 rounded-lg bg-secondary/40 border border-border/60">
                      <div className="p-2 rounded-md bg-background text-primary shrink-0 shadow-2xs">
                        <Icon className="size-4" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground font-default">
                          {item.title}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-normal">
                          {item.desc}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div className="pt-6 border-t border-border/80 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div>
                <p className="text-xs font-medium text-foreground">Need urgent application advice?</p>
                <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                  <a href="mailto:info@antechsolutions.ie" className="flex items-center gap-1 hover:text-foreground transition-colors">
                    <Mail className="size-3.5" />
                    <span>info@antechsolutions.ie</span>
                  </a>
                  <a href="tel:+35312345678" className="flex items-center gap-1 hover:text-foreground transition-colors">
                    <PhoneCall className="size-3.5" />
                    <span>Direct Helpline</span>
                  </a>
                </div>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/BookADemoPage">Book a Demo</Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Form Column (7 cols) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.45,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-7">
            <Card className="border-border shadow-md bg-card">
              <CardHeader className="border-b border-border/60 pb-5">
                <CardTitle className="text-xl sm:text-2xl font-bold font-default text-foreground">
                  Contact Us - Antech Solutions
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Submit your technical parameters or field requirements. Our application specialists will respond with instrument recommendations and feasibility assessments.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formId="contact_form" sectionName="NarcoticsIdentificationCTA" defaultValues={{
                name: "",
                work_email: "",
                company_name: "",
                phone_number: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                work_email: {
                  required: true,
                  pattern: "email"
                },
                company_name: {
                  required: false
                },
                phone_number: {
                  required: false
                },
                message: {
                  required: true
                }
              }} submitText="Request Technical Assessment" successMessage="Thank you. Your narcotics identification inquiry has been received. Our application team will review your requirements and respond promptly." errorMessage="Failed to submit your inquiry. Please verify your entries or contact us directly.">
                  <NarcoticsInquiryForm />
                </FormProvider>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}